import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Layout from "../Layout";
import DietService from "../Services/DietService";
import { message, Spin, Empty } from "antd";
import { FaSyncAlt, FaUpload, FaTimes } from "react-icons/fa";

function DietPlan() {
  const { loggedUser } = useSelector((state) => state.auth);
  const [dietPlans, setDietPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // UI State
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [currentMeal, setCurrentMeal] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    fetchDietPlans();
    return () => window.removeEventListener("resize", handleResize);
  }, [loggedUser]);

  const fetchDietPlans = async () => {
    if (!loggedUser?.patientId) return;
    try {
      setLoading(true);
      const res = await DietService.getDietList({ patientId: loggedUser.patientId });
      if (res.data) {
        setDietPlans(res.data);
      }
    } catch (error) {
      console.error("Error fetching diet plans:", error);
      message.error("Failed to load diet plans");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (meal, newStatus) => {
    try {
      const payload = {
        ...meal,
        status: newStatus,
        modifiedBy: loggedUser?.patientId,
      };
      await DietService.dietPlanUpdate(payload);
      message.success(`Status updated to ${newStatus}`);
      fetchDietPlans();
    } catch (error) {
      message.error("Failed to update status");
    }
  };

  const openUploadModal = (meal) => {
    setCurrentMeal(meal);
    setUploadPreview(meal.mealPhoto || null);
    setUploadFile(null);
    setIsUploadModalOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setUploadPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveUpload = async () => {
    if (!uploadFile && !uploadPreview) {
      message.warning("Please select a file first");
      return;
    }
    try {
      // Assuming dietPlanUpdate can handle base64 or you have a separate upload logic
      // For now, we'll include the preview in the status update payload
      const payload = {
        ...currentMeal,
        mealPhoto: uploadPreview, // Typically you'd upload and get a URL, but using base64 for preview/demo
        modifiedBy: loggedUser?.patientId,
      };
      await DietService.dietPlanUpdate(payload);
      message.success("Photo uploaded successfully");
      setIsUploadModalOpen(false);
      fetchDietPlans();
    } catch (error) {
      message.error("Failed to upload photo");
    }
  };

  const selectedDay = dietPlans[selectedDayIndex] || null;

  return (
    <Layout>
      <div className="w-[90%] sm:w-full mx-auto p-5">
        <div className="flex sm:flex-col items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-teal-900 font-opensans">
              Diet Chart Plan
            </h2>
            <p className="text-xs text-gray-500 mt-1">View and manage your daily nutritional schedule</p>
          </div>

          <div className="flex items-center gap-4 sm:mt-4">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">Select Plan</span>
            <select
              value={selectedDayIndex}
              onChange={(e) => setSelectedDayIndex(Number(e.target.value))}
              className="border border-teal-100 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0CB8A1] bg-white shadow-sm font-medium text-teal-800 min-w-[200px]"
            >
              {dietPlans.length > 0 ? (
                dietPlans.map((dayItem, index) => (
                  <option key={index} value={index}>
                    {dayItem.chartTitle || `Plan ${index + 1}`}
                  </option>
                ))
              ) : (
                <option>No plans available</option>
              )}
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollwidth p-1">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-teal-50">
              <Spin size="large" />
              <p className="mt-4 text-teal-600 animate-pulse font-medium">Fetching your diet plan details...</p>
            </div>
          ) : !selectedDay ? (
            <div className="bg-white rounded-3xl border border-teal-50 shadow-sm p-16 text-center">
              <Empty description={<span className="text-gray-400 font-medium">No diet plan assigned yet.</span>} />
            </div>
          ) : (
            <div className="w-full bg-white rounded-[2rem] border border-gray-100 shadow-xl shadow-teal-900/5 overflow-hidden">
              <div className="flex items-center justify-between px-8 py-5 bg-teal-50/30 border-b border-teal-50">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#0CB8A1] rounded-2xl flex items-center justify-center text-white text-xl shadow-lg shadow-emerald-100">
                    📋
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-teal-900 leading-tight">
                      {selectedDay.chartTitle}
                    </h3>
                    <p className="text-xs text-gray-500">Starts: {selectedDay.fromDate} • {selectedDay.noOfDays} Day(s)</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${selectedDay.planStatus === "Active" ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-100' : 'bg-gray-100 text-gray-400'
                    }`}>
                    {selectedDay.planStatus || "Planned"}
                  </span>
                </div>
              </div>

              {/* ── Desktop Table View ── */}
              {!isMobile && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <colgroup>
                      <col className="w-[120px]" />
                      <col className="w-[120px]" />
                      <col className="w-[160px]" />
                      <col />
                      <col className="w-[160px]" />
                      <col className="w-[120px]" />
                    </colgroup>

                    <thead>
                      <tr className="bg-gray-50/50 text-teal-900 border-b border-gray-100">
                        <th className="px-6 py-5 text-left font-bold uppercase tracking-wider text-[11px]">Day</th>
                        <th className="px-6 py-5 text-left font-bold uppercase tracking-wider text-[11px]">Time</th>
                        <th className="px-6 py-5 text-left font-bold uppercase tracking-wider text-[11px]">Category</th>
                        <th className="px-6 py-5 text-left font-bold uppercase tracking-wider text-[11px]">Meal Details</th>
                        <th className="px-6 py-5 text-center font-bold uppercase tracking-wider text-[11px]">Completion</th>
                        <th className="px-6 py-5 text-left font-bold uppercase tracking-wider text-[11px]">Action</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-50">
                      {selectedDay.patientDietPlanDetails?.map((item, index) => (
                        <tr
                          key={index}
                          className="hover:bg-teal-50/10 transition-colors group"
                        >
                          <td className="px-6 py-5">
                            <span className="font-bold text-teal-600 bg-teal-50 px-3 py-1 rounded-lg text-xs">
                              {item.dayTitle || "Day 1"}
                            </span>
                          </td>
                          <td className="px-6 py-5 font-semibold text-gray-800">{item.mealTime}</td>
                          <td className="px-6 py-5">
                            <span className="px-3 py-1 bg-gray-100 text-gray-500 rounded-full text-[10px] font-black uppercase tracking-widest">
                              {item.mealType}
                            </span>
                          </td>
                          <td className="px-6 py-5 text-gray-700 font-medium">{item.meal}</td>
                          <td className="px-6 py-5 text-center">
                            <select
                              value={item.status}
                              onChange={(e) => handleStatusChange(item, e.target.value)}
                              className="bg-white border border-teal-100 rounded-xl px-3 py-2 text-xs font-bold text-teal-800 outline-none focus:ring-2 focus:ring-teal-500/20 cursor-pointer shadow-sm hover:border-teal-400 transition-all"
                            >
                              <option value="Done">Taken</option>
                              <option value="Skipped">Skipped</option>
                            </select>
                          </td>
                          <td className="px-6 py-5 text-center">
                            <button
                              onClick={() => openUploadModal(item)}
                              className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-sm ${item.mealPhoto
                                ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200'
                                : 'bg-[#0CB8A1] text-white hover:bg-[#089488] hover:scale-110 shadow-lg shadow-emerald-200'
                                }`}
                              title={item.mealPhoto ? "View/Update Photo" : "Upload Photo"}
                            >
                              <FaUpload className="text-sm" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ── Mobile Card View ── */}
              {isMobile && (
                <div className="flex flex-col gap-4 p-6">
                  {selectedDay.patientDietPlanDetails?.map((item, index) => (
                    <div
                      key={index}
                      className="rounded-[2rem] border border-teal-50 bg-white shadow-sm overflow-hidden"
                    >
                      <div className="flex items-center justify-between px-5 py-4 bg-teal-50/30 border-b border-teal-50">
                        <div className="flex items-center gap-3">
                          <span className="w-8 h-8 bg-white rounded-xl flex items-center justify-center text-[10px] font-black text-teal-600 shadow-sm border border-teal-50">
                            {item.dayTitle?.replace("Day ", "") || "1"}
                          </span>
                          <span className="text-xs font-bold text-teal-900">{item.mealTime}</span>
                        </div>
                        <span className="px-3 py-1 bg-white border border-teal-100 rounded-full text-[10px] font-black text-gray-500 uppercase tracking-widest">
                          {item.mealType}
                        </span>
                      </div>
                      <div className="px-5 py-5">
                        <p className="text-sm text-teal-900 font-bold mb-5 leading-relaxed">{item.meal}</p>
                        <div className="flex items-center justify-between gap-4">
                          <select
                            value={item.status}
                            onChange={(e) => handleStatusChange(item, e.target.value)}
                            className="flex-1 bg-teal-50/50 border-none rounded-2xl px-4 py-3 text-xs font-bold text-teal-800 outline-none"
                          >
                            <option value="Done">Taken</option>
                            <option value="Skipped">Skipped</option>
                          </select>
                          <button
                            onClick={() => openUploadModal(item)}
                            className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all ${item.mealPhoto
                              ? 'bg-emerald-100 text-emerald-600'
                              : 'bg-[#0CB8A1] text-white shadow-lg shadow-emerald-200'
                              }`}
                          >
                            <FaUpload className="text-sm" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {isUploadModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-teal-900/40 backdrop-blur-md p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[2rem] w-full max-w-md shadow-2xl overflow-hidden animate-in slide-in-from-bottom-8 duration-500 border border-teal-50">
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Proof of Meal</h3>
                    <p className="text-sm text-gray-500 mt-1">Upload a photo of your {currentMeal?.mealType?.toLowerCase()}</p>
                  </div>
                  <button
                    onClick={() => setIsUploadModalOpen(false)}
                    className="w-10 h-10 rounded-full bg-gray-50 text-gray-400 hover:text-gray-600 flex items-center justify-center transition-colors hover:bg-gray-100"
                  >
                    <FaTimes />
                  </button>
                </div>

                <div className="relative aspect-square w-full bg-teal-50/50 rounded-3xl border-2 border-dashed border-teal-200 flex flex-col items-center justify-center overflow-hidden mb-8 group transition-all hover:border-emerald-400">
                  {uploadPreview ? (
                    <div className="relative w-full h-full group">
                      <img src={uploadPreview} alt="Meal Preview" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <span className="text-white font-medium bg-black/40 backdrop-blur-md px-4 py-2 rounded-full text-xs">Click Change to replace</span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center p-6">
                      <div className="w-20 h-20 bg-white rounded-2xl shadow-sm border border-teal-100 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                        <FaUpload className="text-3xl text-[#0CB8A1]" />
                      </div>
                      <p className="text-sm font-semibold text-teal-800">Choose a photo</p>
                      <p className="text-xs text-gray-400 mt-2">JPG, PNG or WEBP (Max 5MB)</p>
                    </div>
                  )}
                </div>

                <input
                  type="file"
                  id="meal-photo-upload"
                  className="hidden"
                  onChange={handleFileChange}
                  accept="image/*"
                />

                <div className="flex flex-col gap-4">
                  <label
                    htmlFor="meal-photo-upload"
                    className={`w-full py-4 rounded-2xl flex items-center justify-center gap-2 cursor-pointer transition-all font-bold text-sm ${uploadPreview
                      ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      : 'bg-emerald-50 text-[#0CB8A1] border-2 border-emerald-100 hover:bg-emerald-100'
                      }`}
                  >
                    <FaUpload /> {uploadPreview ? "Change Selected Image" : "Upload Image"}
                  </label>

                  <div className="flex gap-4 mt-2">
                    <button
                      onClick={() => setIsUploadModalOpen(false)}
                      className="flex-1 py-4 text-gray-500 font-bold text-sm hover:text-gray-700 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveUpload}
                      disabled={!uploadPreview}
                      className={`flex-[2] py-4 rounded-2xl font-bold text-sm transition-all shadow-xl ${uploadPreview
                        ? 'bg-[#0CB8A1] hover:bg-[#089488] text-white shadow-emerald-200 hover:-translate-y-1'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                        }`}
                    >
                      Confirm & Save Photo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default DietPlan;
